import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';

// components
import Form from '../../components/Form/Form';
import Button from '../../components/Button/Button';
import getItems from '../../api/cars/getItems';
import getFactoryList from '../../api/cars/getFactoryList';
import getTransmissions from '../../api/shop/getTransmissions';
import getContainers from '../../api/cars/getContainers';
import { useNavigate, useParams } from 'react-router-dom';
import checkCrmSidebarNotifications from '../../api/notifications/checkCrmSidebarNotifications';
import HookForm from '../../components/HookForm/HookForm';
import ForeignCityNotice from '../../components/ForeignCity/ForeignCityNotice';
import SelectMarks from '../../components/Selects/SelectMarks';
import SelectModels from '../../components/Selects/SelectModels';
import SelectComplectations from '../../components/Selects/SelectComplectations';
import Loader from '../../components/Loader/Loader';
import checkStockCar from '../../utils/checkStockCar';
import SelectCarTransmissions from '../../components/Selects/SelectCarTransmissions';
import formatNumber from '../../utils/formatNumber';
import SelectCity from '../../components/Selects/SelectCity';
import SelectDc from '../../components/Selects/SelectDc';
import Datepicker from '../../components/Inputs/Datepicker/Datepicker';
import editCar from '../../api/cars/editCar';
import alert from '../../helpers/alert';
import dayjs from 'dayjs';
import {
  CAR_ORIGIN_LABELS,
  CAR_ORIGIN_TYPES,
  USED_CAR_FACTORY_ID,
  PURCHASE_PROCESS_TYPES,
  isConsignmentOrigin,
} from '../../constants/carOrigin';
import sortFactoryList from '../../utils/sortFactoryList';
import { originAvailable } from '../../brand/tenant';
import Select from '../../components/Select/Select';

const formStyles = {
  carInfo: { display: 'grid', gap: '20px', gridTemplateColumns: `1fr 1fr 1fr 1fr`, alignItems: 'flex-end' },
  dealerInfo: { display: 'grid', gap: '20px', gridTemplateColumns: '1fr 1fr' },
  other: { display: 'grid', gap: '20px' },
};

// Объём двигателя хранится в двух местах: в карточке машины — в кубических
// сантиметрах, в комплектации — в литрах. Расхождение означает, что банк не найдёт
// машину в своём справочнике, поэтому показываем его подсказкой прямо под полем.
const toLiters = (value) => {
  const parsed = Number(String(value ?? '').replace(',', '.').replace(/[^0-9.].*$/, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round((parsed > 100 ? parsed / 1000 : parsed) * 10) / 10;
};

// Показываем в кубических сантиметрах: менеджер правит поле именно в них, и
// значение из комплектации переносится как есть, без пересчёта в уме.
const toCubic = (value) => {
  const liters = toLiters(value);
  if (liters === null) return null;
  const parsed = Number(String(value ?? '').replace(',', '.').replace(/[^0-9.].*$/, ''));
  return parsed > 100 ? Math.round(parsed) : Math.round(liters * 1000);
};

const engineVolumeHint = (car) => {
  const inCard = toLiters(car?.engine_volume);
  const inComplectation = toLiters(car?.complectation?.engine_volume);
  if (inCard === null || inComplectation === null) return undefined;
  if (Math.abs(inCard - inComplectation) <= 0.15) return undefined;

  return `В комплектации указан объём ${toCubic(car?.complectation?.engine_volume)}, а в карточке — ${toCubic(
    car?.engine_volume
  )}. ForteBank не найдёт машину в справочнике, если объём не совпадёт`;
};

const CarMainData = ({ containerId, onSaved }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [carInfo, setCarInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [factoryList, setFactoryList] = useState([]);
  // Справочник заводов на JetQ пустой — карточка не должна ждать его вечно.
  const [factoryListLoaded, setFactoryListLoaded] = useState(false);
  const [factoryId, setFactoryId] = useState(null);
  const resolvedContainerIdRef = useRef(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const resolveContainerId = async ({ markOption, modelOption }) => {
    if (!markOption?.label || !modelOption?.label) {
      resolvedContainerIdRef.current = null;
      return;
    }
    try {
      const query = `${markOption.label} ${modelOption.label}`;
      const res = await getContainers({ markAndModel: query, page: 1 });
      const container = res?.data?.containers?.[0];
      resolvedContainerIdRef.current = container?.id ?? null;
    } catch (e) {
      console.error('Ошибка определения containerId по марке/модели', e);
      resolvedContainerIdRef.current = null;
    }
  };

  const dealerInfo = [
    { type: 'input', value: 'Kia DOSCAR', label: 'Наименование дилерского центра', disabled: true },
    { type: 'input', value: 'г. Алматы, ул. Елебекова 10/1', label: 'Адрес дилерского центра', disabled: true },
    { type: 'input', value: '01.10.2022', label: 'Дата прибытия в дилерский центр', disabled: true },
    { type: 'input', value: '10%', label: 'Скидка дилерского центра', disabled: true },
  ];

  const other = [
    { type: 'input', value: 'Бахытжан Шагабутдинов', label: 'ФИО ответственного менеджера', disabled: true },
    { type: 'input', value: 'Какой-то комментарий', label: 'Заметки/комментарии об автомобиле', disabled: true },
  ];
  const [car, setCar] = useState({});

  const loadFactoryList = async () => {
    try {
      const response = await getFactoryList();
      if (response?.data?.data) {
        setFactoryList(sortFactoryList(response.data.data));
      }
    } catch (error) {
      console.error('Ошибка загрузки списка заводов:', error);
    } finally {
      setFactoryListLoaded(true);
    }
  };

  const originOptions = useMemo(() => {
    const baseOptions = Object.keys(CAR_ORIGIN_TYPES)
      .filter((key) => CAR_ORIGIN_TYPES[key] !== CAR_ORIGIN_TYPES.NEW_KZ)
      .filter((key) => originAvailable(CAR_ORIGIN_TYPES[key]))
      .map((key) => ({
        value: CAR_ORIGIN_TYPES[key],
        label: CAR_ORIGIN_LABELS[CAR_ORIGIN_TYPES[key]],
      }));

    if (factoryList.length > 0) {
      const factoryOptions = factoryList.map((factory) => ({
        value: `factory_${factory.id}`,
        label: factory.id === USED_CAR_FACTORY_ID ? factory.name : 'Новое авто, ' + factory.name,
        factoryId: factory.id,
        isUsedFactory: factory.id === USED_CAR_FACTORY_ID,
      }));
      return [...factoryOptions, ...baseOptions];
    } else {
      return [
        {
          value: CAR_ORIGIN_TYPES.NEW_KZ,
          label: CAR_ORIGIN_LABELS[CAR_ORIGIN_TYPES.NEW_KZ],
        },
        ...baseOptions,
      ];
    }
  }, [factoryList]);

  const init = useCallback(() => {
    setLoading(true);
    setCarInfo([]);
    getItems({ id: containerId ?? id })
      .then(async (res) => {
        const car = res.data.cars[0];
        setCar(car);

        // Расхождение объёма с комплектацией банк не простит: он ищет машину в
        // справочнике по марке, модели, году и объёму сразу. Сообщаем здесь, пока
        // машину не выбрали в сделку — на шаге заявки исправлять уже поздно.
        const volumeHint = engineVolumeHint(car);
        if (volumeHint) {
          alert.error(volumeHint, { autoClose: false, closeOnClick: false });
        }
        resolvedContainerIdRef.current = car?.container?.id ?? null;
        
        let transmissionDefaultValue = null;
        if (car?.transmission?.id) {
          transmissionDefaultValue = { value: car.transmission.id, label: car.transmission.title };
        } else if (car?.transmissionId) {
          try {
            const transRes = await getTransmissions();
            const found = transRes.data.find(t => t.id === car.transmissionId);
            if (found) {
              transmissionDefaultValue = { value: found.id, label: found.title };
            }
          } catch (e) {
            console.error('Ошибка загрузки transmissions:', e);
          }
        }
        
        let originDefaultValue;
        if (car?.originCarId === CAR_ORIGIN_TYPES.NEW_KZ && car?.factoryId) {
          const factory = factoryList.find((f) => f.id === car.factoryId);
          if (factory) {
          originDefaultValue = {
              value: `factory_${factory.id}`,
              label: factory.id === USED_CAR_FACTORY_ID ? factory.name : 'Новое авто, ' + factory.name,
              factoryId: factory.id,
              isUsedFactory: factory.id === USED_CAR_FACTORY_ID,
            };
            setFactoryId(car.factoryId);
          } else {
            originDefaultValue = {
              value: CAR_ORIGIN_TYPES.NEW_KZ,
              label: CAR_ORIGIN_LABELS[CAR_ORIGIN_TYPES.NEW_KZ],
            };
          }
        } else {
          originDefaultValue = {
            value: car?.originCarId,
            label: CAR_ORIGIN_LABELS[car?.originCarId],
          };
        }

        // Текущие ДЦ машины. Поддерживаем разные формы поля с бэка:
        // массив id ([5, 7]) либо массив объектов ([{ id, name }] / [{ dcId }]).
        // Авто по договору поручения редактируем теми же правилами, что и создаём:
        // ровно один ДЦ и только АСП, плюс поля техпаспорта.
        const isConsignment = isConsignmentOrigin(car?.originCarId);
        // Список ДЦ по типу машины: БУ (выкуп, мена, поручение, завод БУ) — только
        // АСП, новое авто — только салоны. Иначе новой машине можно было поставить
        // АСП, а БУ — салон, и банки получали не ту точку.
        const isCarUsedNow =
          car?.used || car?.factoryId === USED_CAR_FACTORY_ID || PURCHASE_PROCESS_TYPES.includes(car?.originCarId);

        const currentDc = car?.dcIds || car?.dcs || car?.dealerCenters || [];
        const dcDefaultValue = (Array.isArray(currentDc) ? currentDc : [])
          .map((dc) => {
            if (dc == null) return null;
            if (typeof dc === 'object') {
              const value = dc.id ?? dc.dcId ?? dc.value;
              if (value == null) return null;
              return { value, label: dc.name ?? dc.label ?? String(value) };
            }
            return { value: dc, label: String(dc) };
          })
          .filter(Boolean);

        const carInfo = [
          {
            component: Select,
            name: 'origin',
            label: 'Происхождение автомобиля',
            placeholder: 'Выберите тип происхождения',
            options: originOptions,
            rules: { required: true },
            defaultValue: originDefaultValue,
            onChangeCustom: (selectedOption) => {
              const newValue = selectedOption?.value;
              if (typeof newValue === 'string' && newValue.startsWith('factory_')) {
                const selectedFactoryId = selectedOption.factoryId;
                setFactoryId(selectedFactoryId);
              } else {
                setFactoryId(null);
              }
            },
          },
          {
            name: 'mark',
            component: SelectMarks,
            label: 'Марка',
            placeholder: 'Выберите из списка',
            defaultValue: { value: car?.container?.mark?.id, label: car?.container?.mark?.name },
            onChangeCustom: () => {
              resolvedContainerIdRef.current = null;
            },
          },
          {
            name: 'model',
            component: SelectModels,
            dependency: 'mark',
            dependencyAccessor: (obj) => obj?.value,
            label: 'Модель',
            placeholder: 'Выберите из списка',
            defaultValue: { value: car?.container?.model?.id, label: car?.container?.model?.name },
            onChangeCustom: (modelOption, markOption) => {
              resolveContainerId({ markOption, modelOption });
            },
          },
          {
            name: 'complect',
            component: SelectComplectations,
            dependency: ['mark', 'model'],
            dependencyAccessor: (arr) => arr?.map((o) => o?.value).join('|'),
            label: 'Комплектация',
            placeholder: 'Выберите из списка',
            defaultValue: { value: car?.complectation?.id, label: car?.complectation?.title },
            params: () => resolvedContainerIdRef.current,
          },
          {
            name: 'issueYear',
            label: 'Год выпуска',
            defaultValue: car.issueYear,
            rules: {
              validate: (value) => {
                if (value < 1900 || value > dayjs().year()) {
                  return 'Недопустимый год выпуска';
                }

                return true;
              },
            },
          },
          { name: 'vin', label: 'VIN', defaultValue: car.vin },
          ...(isConsignment
            ? [
                {
                  name: 'texNumber',
                  label: 'Номер техпаспорта',
                  placeholder: 'Укажите номер',
                  defaultValue: car?.texNumber,
                },
                {
                  name: 'texDate',
                  component: Datepicker,
                  label: 'Дата выдачи техпаспорта',
                  placeholder: 'дд-мм-гггг',
                  defaultValue: car?.texDate,
                },
              ]
            : []),
          { name: 'color', label: 'Цвет кузова', color: car.color, defaultValue: car.color },
          { name: 'engine_volume', label: 'Объем двигателя', defaultValue: car.engine_volume,
            // Объём указывается в кубических сантиметрах и уходит в банки: они ищут
            // машину в справочнике по марке, модели, году и объёму сразу. Ошибка здесь
            // означает отказ «модель не найдена в справочнике» уже после отправки заявки.
            rules: {
              validate: (value) => {
                if (!value.match(/^[0-9]+$/)) {
                  return 'Недопустимый формат';
                }

                const liters = Number(value) / 1000;
                if (liters < 0.6 || liters > 8) {
                  return 'Похоже на опечатку: укажите объём в кубических сантиметрах, например 1499';
                }

                return true;
              },
            }
          },
          { name: 'engine', label: 'Тип двигателя', defaultValue: car?.complectation?.engine?.title },
          {
            name: 'transmission',
            component: SelectCarTransmissions,
            label: 'Тип коробки передач',
            defaultValue: transmissionDefaultValue,
          },
          {
            name: 'mileage',
            label: 'Пробег, км',
            defaultValue: car?.mileage,
            format: 'number',
            rules: {
              validate: (value, getValues) => {
                const origin = getValues('origin');
                const originValue = origin?.value;
                const isUsed = typeof originValue === 'string' && originValue.startsWith('factory_')
                  ? origin?.isUsedFactory
                  : PURCHASE_PROCESS_TYPES.includes(originValue);
                if (isUsed && (!value || value.toString().trim() === '')) {
                  return 'Пробег обязателен для б/у автомобилей';
                }
                return true;
              },
            },
          },
          { name: 'price', label: 'РРЦ, KZT', defaultValue: formatNumber(car?.price), format: 'number' },
          {
            component: SelectCity,
            name: 'city',
            label: 'Город',
            defaultValue: { value: car?.city?.id, label: car?.city?.title },
          },
          {
            component: SelectDc,
            name: 'dc',
            label: 'Дилерский центр',
            placeholder: 'Выберите из списка',
            isMulti: !isConsignment,
            used: !!isCarUsedNow,
            defaultValue: isConsignment ? dcDefaultValue[0] ?? null : dcDefaultValue,
          },
          {
            name: 'status',
            label: 'Статус',
            defaultValue: checkStockCar(car?.checkStock) ? 'В наличии' : 'Нет в наличии',
            disabled: true,
          },
        ];

        setCarInfo(carInfo);
        checkCrmSidebarNotifications({ notificationIds: car?.notifications?.map((i) => i?.id) });
        onSavedRef.current?.(car);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [factoryList, originOptions, containerId, id]);

  useEffect(() => {
    loadFactoryList();
  }, []);

  // Машину грузим после ответа справочника заводов, а не после непустого списка:
  // на JetQ заводов нет, и карточка крутила спиннер бесконечно (машина 146).
  React.useEffect(() => {
    if (factoryListLoaded) {
      init();
    }
  }, [factoryListLoaded, init]);

  const sellCar = () => {
    navigate('/deals/create', { state: { car } });
  };

  const saveCar = (payload) => {
    if (!payload?.vin || payload?.vin?.trim()?.length !== 17) {
      alert.error('Длина VIN должна быть 17 символов');
      return;
    }

    setLoading(true);
    
    const originValue = payload?.origin?.value;
    const finalOrigin = typeof originValue === 'string' && originValue.startsWith('factory_') 
      ? CAR_ORIGIN_TYPES.NEW_KZ 
      : originValue;

    const isUsedFactory = typeof originValue === 'string' && originValue.startsWith('factory_')
      ? payload?.origin?.isUsedFactory || factoryId === USED_CAR_FACTORY_ID
      : false;
    // Та же логика, что при создании (CarCreate): выкуп у клиента, залоговое,
    // мена и договор поручения — это БУ. Раньше при сохранении карточки флаг
    // ставился только для завода БУ, и машина «по договору поручения» после
    // обычного сохранения уходила в банки как новая (сделка 191340).
    const isCarUsed = typeof originValue === 'string' && originValue.startsWith('factory_')
      ? isUsedFactory
      : PURCHASE_PROCESS_TYPES.includes(finalOrigin);

    const editPayload = {
      ...payload,
      price: parseInt(payload?.price?.replace(/\s/g, '')),
      complectationId: payload?.complect?.value,
      transmissionId: payload?.transmission?.value,
      cityId: payload?.city?.value,
      markId: payload?.mark?.value,
      modelId: payload?.model?.value,
      vin: payload?.vin?.trim(),
      originCarId: finalOrigin,
      used: isCarUsed,
      mileage: payload?.mileage ? parseInt(payload?.mileage?.replace(/\s/g, '')) : undefined,
      factoryId,
      dcIds: [].concat(payload?.dc || []).map((dc) => dc?.value).filter((v) => v != null),
      texDate: payload?.texDate ? dayjs(payload.texDate).format('YYYY-MM-DD') : undefined,
    };

    editCar(containerId || id, editPayload)
      .then((res) => {
        init();
        alert.success('Данные успешно сохранены');
      })
      .catch((err) => {
        alert.error(err?.response?.data?.errorMessage || 'Произошла ошибка при сохранении данных');
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div style={{ paddingBottom: '20px' }}>
          <ForeignCityNotice city={car?.city} />
          {/* <Form inputs={dealerInfo} title={'Информация о дилерском центре'} grid={formStyles.dealerInfo} border />
      <Form inputs={other} title={'Дополнительно'} grid={formStyles.other} border /> */}
          <HookForm inputs={carInfo} border grid={formStyles.carInfo} onSubmit={saveCar} action="Сохранить изменения" />
          {car?.mark && !containerId && checkStockCar(car?.checkStock) ? (
            <Button text={'Продать автомобиль'} fill onClick={sellCar} />
          ) : null}
        </div>
      )}
    </>
  );
};

export default CarMainData;
